import React, { useState } from 'react';
import {
    Box, Typography, Chip, Divider, IconButton, Tooltip, Button,
    useTheme, useMediaQuery, TextField,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import HeightIcon from '@mui/icons-material/Height';
import TimerIcon from '@mui/icons-material/Timer';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PublicIcon from '@mui/icons-material/Public';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AddCommentIcon from '@mui/icons-material/AddComment';
import NoteIcon from '@mui/icons-material/Note';
import MapIcon from '@mui/icons-material/Map';

/* ─── Status config ─────────────────────────────────────── */
const STATUS_CONFIG = {
    'Authorized': { color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
    'Pending': { color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
    'Complete': { color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
    'De-authorized': { color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
    'Deleted': { color: '#546e7a', bg: '#eceff1', border: '#b0bec5' },
    'Rescinded Awaiting': { color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
    'Never Authorized': { color: '#795548', bg: '#efebe9', border: '#bcaaa4' },
    'Invalid Detected': { color: '#f57f17', bg: '#fff9c4', border: '#fff176' },
    'No Authorization': { color: '#388e3c', bg: '#f1f8e9', border: '#c5e1a5' },
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG['Pending'];

/* ─── File icon helper ───────────────────────────────────── */
const getFileIcon = (fileName = '') => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 18, color: '#e53935' }} />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext))
        return <ImageIcon sx={{ fontSize: 18, color: '#1e88e5' }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 18, color: '#546e7a' }} />;
};

/* ─── Info row ───────────────────────────────────────────── */
const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
        <Box sx={{ color: '#8a96a3', mt: 0.1, flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#8a96a3', display: 'block', lineHeight: 1.2, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e', wordBreak: 'break-word' }}>
                {value || '—'}
            </Typography>
        </Box>
    </Box>
);

/* ─── Section heading ────────────────────────────────────── */
const SectionHeading = ({ children }) => (
    <Typography variant="overline" sx={{ color: '#8a96a3', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 1.2, display: 'block', mb: 1 }}>
        {children}
    </Typography>
);

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
const OperationDetailPanel = ({
    operation,
    files = [],
    onCancel,
    onDelete,
    onRescind,
    onInvalidCancel,
    onCreateTemplate,
    onDownload,
    onShare,
    onCheckHistory,
    onDeleteFile,
    onUploadClick,
    isReadOnly = false,
    onRedirect,
    onEdit,
    onAddNote,
    onViewOnMap,
}) => {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [noteText, setNoteText] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState('');

    if (!operation) return null;

    const showActions = !isReadOnly;
    const primaryAuth = operation.authorizations?.[0] ?? null;
    const status = primaryAuth?.status ?? 'No Authorization';
    const sc = getStatusConfig(status);

    /* ── action buttons config ── */
    const actions = showActions ? [
        ...(status === 'Authorized' && onCancel ? [
            { label: 'Cancel', icon: <CancelIcon sx={{ fontSize: 16 }} />, color: 'error', variant: 'outlined', onClick: () => onCancel(primaryAuth.authorization_id) },
        ] : []),
        ...(['De-authorized', 'Complete'].includes(status) && onDelete ? [
            { label: 'Delete', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, color: 'error', variant: 'outlined', onClick: () => onDelete(primaryAuth.authorization_id) },
        ] : []),
        ...(status === 'Rescinded Awaiting' && onRescind ? [
            { label: 'Rescind', icon: <UndoIcon sx={{ fontSize: 16 }} />, color: 'warning', variant: 'outlined', onClick: () => onRescind(primaryAuth.authorization_id) },
        ] : []),
        ...(status === 'Invalid Detected' && onInvalidCancel ? [
            { label: 'Invalid Cancel', icon: <ReportProblemIcon sx={{ fontSize: 16 }} />, color: 'warning', variant: 'contained', onClick: () => onInvalidCancel(primaryAuth.authorization_id) },
        ] : []),
        ...(onCreateTemplate ? [
            { label: 'Save Template', icon: <BookmarkAddIcon sx={{ fontSize: 16 }} />, color: 'primary', variant: 'outlined', onClick: () => onCreateTemplate(operation) },
        ] : []),
    ] : [];

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', pb: 3 }}>
            {/* ── Hero header ───────────────────────────────── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${sc.bg} 0%, #fff 100%)`,
                    border: `1px solid ${sc.border}`,
                    borderRadius: 3,
                    p: 3,
                    mb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* decorative blur circle */}
                <Box sx={{
                    position: 'absolute', top: -30, right: -30,
                    width: 120, height: 120, borderRadius: '50%',
                    background: sc.color, opacity: 0.07,
                }} />

                {/* Operation ID + Status */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: '#8a96a3', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Operation ID
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', letterSpacing: -0.5, lineHeight: 1 }}>
                            FLZ{operation.operation_id}
                        </Typography>
                    </Box>
                    <Chip
                        label={status}
                        size="small"
                        sx={{
                            bgcolor: sc.bg,
                            color: sc.color,
                            border: `1px solid ${sc.border}`,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            px: 0.5,
                        }}
                    />
                </Box>

                {/* Quick info pills */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                        { icon: <CalendarTodayIcon sx={{ fontSize: 13 }} />, text: operation.date },
                        { icon: <AccessTimeIcon sx={{ fontSize: 13 }} />, text: `${operation.start_time}${operation.timezone ? ' ' + operation.timezone : ''}` },
                        { icon: <TimerIcon sx={{ fontSize: 13 }} />, text: `${operation.duration ?? '—'} min` },
                    ].map(({ icon, text }) => (
                        <Box key={text} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: 'rgba(255,255,255,0.75)', borderRadius: 2,
                            px: 1.25, py: 0.4, border: '1px solid #e5e8ec',
                            backdropFilter: 'blur(4px)',
                        }}>
                            <Box sx={{ color: sc.color }}>{icon}</Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.75rem' }}>{text}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── Details grid ──────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid #e5e8ec', p: 2.5, mb: 2 }}>
                <SectionHeading>Operation Details</SectionHeading>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: 0,
                }}>
                    <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />} label="Operator" value={operation.operator_name} />
                    <InfoRow icon={<FlightTakeoffIcon sx={{ fontSize: 16 }} />} label="Op Ceiling" value={operation.altitude ? `${operation.altitude} ft AGL` : null} />
                </Box>
            </Box>

            {/* ── Authorizations ────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid #e5e8ec', p: 2.5, mb: 2 }}>
                <SectionHeading>Authorizations</SectionHeading>
                {operation.authorizations?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {operation.authorizations.map((auth, i) => {
                            const ast = getStatusConfig(auth.status);
                            return (
                                <Box
                                    key={auth.authorization_id ?? i}
                                    sx={{
                                        border: `1px solid ${ast.border}`,
                                        borderRadius: 2,
                                        p: 2,
                                        bgcolor: ast.bg,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    {/* Auth header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <VerifiedUserIcon sx={{ fontSize: 16, color: ast.color }} />
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: ast.color }}>
                                                {auth.laanc_code
                                                    ?? auth.authorizing_airport_code
                                                    ?? `AUTH-${auth.authorization_id}`}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={auth.status}
                                            size="small"
                                            sx={{ bgcolor: '#fff', color: ast.color, border: `1px solid ${ast.border}`, fontWeight: 600, fontSize: '0.7rem', borderRadius: '6px' }}
                                        />
                                    </Box>

                                    {/* Auth details row */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        {(auth.facility || auth.authorizing_airport_name) && (
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#8a96a3', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Airport / Facility</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                                    {auth.authorizing_airport_name
                                                        ? `${auth.authorizing_airport_name} (${auth.authorizing_airport_code})`
                                                        : auth.facility}
                                                </Typography>
                                            </Box>
                                        )}
                                        {auth.authorization_start_time && (
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#8a96a3', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Start</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{auth.authorization_start_time}</Typography>
                                            </Box>
                                        )}
                                        {auth.duration && (
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#8a96a3', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Duration</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{auth.duration} min</Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Auth action */}
                                    {showActions && auth.status === 'Authorized' && onCancel && (
                                        <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => onCancel(auth.authorization_id)} sx={{ alignSelf: 'flex-start', fontSize: '0.75rem', py: 0.4 }}>
                                            Cancel Authorization
                                        </Button>
                                    )}
                                    {showActions && ['De-authorized', 'Complete'].includes(auth.status) && onDelete && (
                                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => onDelete(auth.authorization_id)} sx={{ alignSelf: 'flex-start', fontSize: '0.75rem', py: 0.4 }}>
                                            Delete
                                        </Button>
                                    )}
                                    {showActions && auth.status === 'Rescinded Awaiting' && onRescind && (
                                        <Button size="small" variant="outlined" color="warning" startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => onRescind(auth.authorization_id)} sx={{ alignSelf: 'flex-start', fontSize: '0.75rem', py: 0.4 }}>
                                            Acknowledge Rescind
                                        </Button>
                                    )}
                                    {showActions && auth.status === 'Invalid Detected' && onInvalidCancel && (
                                        <Button size="small" variant="contained" color="warning" startIcon={<ReportProblemIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => onInvalidCancel(auth.authorization_id)} sx={{ alignSelf: 'flex-start', fontSize: '0.75rem', py: 0.4 }}>
                                            Acknowledge Invalid
                                        </Button>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ py: 2.5, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No LAANC authorizations required for this operation area.
                        </Typography>
                    </Box>
                )}
            </Box>


            {/* ── Quick Actions footer ───────────────────────── */}
            {showActions && actions.length > 0 && (
                <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid #e5e8ec', p: 2.5 }}>
                    <SectionHeading>Quick Actions</SectionHeading>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {actions.map(({ label, icon, color, variant, onClick }) => (
                            <Button
                                key={label}
                                size="small"
                                variant={variant}
                                color={color}
                                startIcon={icon}
                                onClick={onClick}
                                sx={{ fontSize: '0.8rem', borderRadius: 2, textTransform: 'none', py: 0.75 }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            )}

            {/* ── Calendar Notes Section ───────────────────────── */}
            {isReadOnly && onAddNote && (
                <Box sx={{
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    p: 2.5,
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NoteIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                Operation Notes
                            </Typography>
                        </Box>
                        {!addingNote && (!Array.isArray(operation.calendarNotes) || operation.calendarNotes.length === 0) && (
                            <Button
                                size="small"
                                startIcon={<AddCommentIcon />}
                                onClick={() => setAddingNote(true)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: '#1976d2',
                                    fontSize: '0.78rem',
                                }}
                            >
                                Add Note
                            </Button>
                        )}
                    </Box>

                    {/* Existing notes — each is viewable and editable */}
                    {Array.isArray(operation.calendarNotes) && operation.calendarNotes.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: addingNote ? 1.5 : 0 }}>
                            {operation.calendarNotes.map((note, idx) => (
                                <Box key={note.id || idx} sx={{
                                    bgcolor: '#e1f5fe',
                                    border: '1px solid #b3e5fc',
                                    borderRadius: 2,
                                    p: 1.5
                                }}>
                                    {editingNoteId === (note.id || idx) ? (
                                        /* ── Inline edit mode ── */
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <TextField
                                                multiline
                                                minRows={2}
                                                maxRows={5}
                                                fullWidth
                                                value={editingNoteText}
                                                onChange={e => setEditingNoteText(e.target.value)}
                                                size="small"
                                                autoFocus
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        fontSize: '0.875rem',
                                                    }
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                                                    sx={{ textTransform: 'none', color: '#64748b' }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={!editingNoteText.trim() || savingNote}
                                                    onClick={async () => {
                                                        if (!editingNoteText.trim()) return;
                                                        setSavingNote(true);
                                                        await onAddNote({
                                                            id: note.id,
                                                            date: note.date,
                                                            startTime: note.startTime,
                                                            description: editingNoteText.trim(),
                                                            operationId: operation.id,
                                                            _isEdit: true
                                                        });
                                                        setEditingNoteId(null);
                                                        setEditingNoteText('');
                                                        setSavingNote(false);
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        borderRadius: 2,
                                                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                                    }}
                                                >
                                                    {savingNote ? 'Saving...' : 'Save'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        /* ── View mode ── */
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Typography variant="body2" sx={{ color: '#01579b', fontWeight: 500, flex: 1 }}>
                                                    {note.description}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingNoteId(note.id || idx);
                                                        setEditingNoteText(note.description || '');
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        color: '#1976d2',
                                                        fontSize: '0.72rem',
                                                        minWidth: 'auto',
                                                        ml: 1,
                                                        py: 0,
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                                                {note.date} {note.startTime && `at ${note.startTime}`}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        !addingNote && (
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.82rem' }}>
                                No notes yet. Add one to annotate this operation.
                            </Typography>
                        )
                    )}

                    {/* Inline add note form — uses the operation's own date & time */}
                    {addingNote && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            <TextField
                                multiline
                                minRows={2}
                                maxRows={5}
                                fullWidth
                                placeholder="Write your note here..."
                                value={noteText}
                                onChange={e => setNoteText(e.target.value)}
                                size="small"
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        fontSize: '0.875rem',
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={() => { setAddingNote(false); setNoteText(''); }}
                                    sx={{ textTransform: 'none', color: '#64748b' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!noteText.trim() || savingNote}
                                    onClick={async () => {
                                        if (!noteText.trim()) return;
                                        setSavingNote(true);
                                        // Use the operation's own date and start_time
                                        const opDate = operation.date
                                            ? operation.date.split('T')[0]
                                            : new Date().toISOString().split('T')[0];
                                        const opTime = operation.start_time
                                            ? operation.start_time.substring(0, 5)
                                            : new Date().toTimeString().substring(0, 5);
                                        await onAddNote({
                                            date: opDate,
                                            startTime: opTime,
                                            description: noteText.trim(),
                                            operationId: operation.id
                                        });
                                        setNoteText('');
                                        setAddingNote(false);
                                        setSavingNote(false);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    }}
                                >
                                    {savingNote ? 'Saving...' : 'Save Note'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* ── Files (Moved below Operation Notes) ──────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid #e5e8ec', p: 2.5, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <SectionHeading>Files</SectionHeading>
                    {showActions && onUploadClick && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FileUploadIcon sx={{ fontSize: 15 }} />}
                            onClick={onUploadClick}
                            sx={{ fontSize: '0.75rem', py: 0.5, textTransform: 'none', borderRadius: 2 }}
                        >
                            Upload
                        </Button>
                    )}
                </Box>

                {files.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {files.map((f) => (
                            <Box
                                key={f.fileId}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: '1px solid #e5e8ec',
                                    bgcolor: '#fafbfc',
                                    transition: 'background 0.15s',
                                    '&:hover': { bgcolor: '#f0f2f5' },
                                }}
                            >
                                <Box sx={{ flexShrink: 0 }}>{getFileIcon(f.fileName)}</Box>
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }} title={f.fileName}>
                                    {f.fileName}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                    {onDownload && (
                                        <Tooltip title="Download" arrow>
                                            <IconButton size="small" onClick={() => onDownload(f)} sx={{ color: '#546e7a', p: 0.5 }}>
                                                <DownloadIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {showActions && onShare && (
                                        <Tooltip title="Share" arrow>
                                            <IconButton size="small" onClick={() => onShare(f)} sx={{ color: '#1976d2', p: 0.5 }}>
                                                <ShareIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {showActions && onCheckHistory && (
                                        <Tooltip title="Share History" arrow>
                                            <IconButton size="small" onClick={() => onCheckHistory(f)} sx={{ color: '#546e7a', p: 0.5 }}>
                                                <HistoryIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {showActions && onDeleteFile && (
                                        <Tooltip title="Delete File" arrow>
                                            <IconButton size="small" onClick={() => onDeleteFile(f.fileName)} sx={{ color: '#e53935', p: 0.5 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No files attached to this operation.</Typography>
                    </Box>
                )}
            </Box>

            {/* ── Redirection Prompt (for ReadOnly mode) ─────── */}
            {isReadOnly && (
                <Box sx={{
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    border: '1px dashed #cbd5e1',
                    p: 2.5,
                    textAlign: 'center',
                    mt: -1, // collapse gap
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    {onEdit && operation.operator_name === 'Reminder' && (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => onEdit(operation)}
                            startIcon={<NotificationsActiveIcon />}
                            sx={{
                                borderRadius: 2.5,
                                textTransform: 'none',
                                py: 1,
                                fontWeight: 700,
                                borderColor: '#1e3a8a',
                                color: '#1e3a8a',
                                '&:hover': {
                                    borderColor: '#172554',
                                    bgcolor: 'rgba(30, 58, 138, 0.04)'
                                }
                            }}
                        >
                            Edit Task Description
                        </Button>
                    )}

                    {onViewOnMap && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => onViewOnMap(operation)}
                            startIcon={<MapIcon />}
                            sx={{
                                bgcolor: '#0ea5e9',
                                py: 1.25,
                                borderRadius: 2.5,
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.2)',
                                '&:hover': {
                                    bgcolor: '#0284c7',
                                    boxShadow: '0 6px 16px rgba(14, 165, 233, 0.3)',
                                }
                            }}
                        >
                            View on Map (Beta)
                        </Button>
                    )}

                    {onRedirect && (
                        <>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 0, fontWeight: 500 }}>
                                {operation.operator_name === 'Reminder'
                                    ? "This task can also be managed in the main Mission Log view."
                                    : "Additional actions, file uploads, and templates are available in the main Mission Log view."}
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={onRedirect}
                                startIcon={<HistoryIcon />}
                                sx={{
                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.2)',
                                    borderRadius: 2.5,
                                    textTransform: 'none',
                                    py: 1,
                                    fontWeight: 700,
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #1e3a8a 20%, #2563eb 100%)',
                                    }
                                }}
                            >
                                Manage in Mission Log
                            </Button>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default OperationDetailPanel;
